import {
  Renderer,
  RenderTarget,
  Geometry,
  Mesh,
  Program,
  OGLRenderingContext,
} from "ogl";

/**
 * Cubic ease-in-out function for smooth animation transitions.
 *
 * @param t - Normalized time (between 0 and 1)
 * @returns The eased value (between 0 and 1)
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Downloads the current canvas as a PNG.
 * This function captures the current state of the canvas and downloads it as a PNG file.
 *
 * @param canvas - The HTML canvas element to capture
 * @param renderer - The WebGL renderer instance (optional)
 */
export function downloadCanvasAsPNG({
  canvas,
  renderer,
}: {
  canvas: HTMLCanvasElement;
  renderer?: Renderer | null;
}) {
  if (renderer) {
    const gl = renderer.gl;
    const pixels = new Uint8Array(canvas.width * canvas.height * 4);
    gl.readPixels(
      0,
      0,
      canvas.width,
      canvas.height,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels
    );

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d");
    if (!tempCtx) return;

    // Fill with white background
    tempCtx.fillStyle = "#FFFFFF";
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Create and flip the image data
    const imageData = new ImageData(
      new Uint8ClampedArray(pixels),
      canvas.width,
      canvas.height
    );
    const flippedImageData = new ImageData(
      new Uint8ClampedArray(imageData.data),
      imageData.width,
      imageData.height
    );

    // Flip the image vertically (WebGL and Canvas have different coordinate systems)
    for (let y = 0; y < imageData.height; y++) {
      for (let x = 0; x < imageData.width; x++) {
        const srcIndex = (y * imageData.width + x) * 4;
        const dstIndex = ((imageData.height - 1 - y) * imageData.width + x) * 4;
        flippedImageData.data[dstIndex] = imageData.data[srcIndex];
        flippedImageData.data[dstIndex + 1] = imageData.data[srcIndex + 1];
        flippedImageData.data[dstIndex + 2] = imageData.data[srcIndex + 2];
        flippedImageData.data[dstIndex + 3] = imageData.data[srcIndex + 3];
      }
    }

    tempCtx.putImageData(flippedImageData, 0, 0);

    // Create and trigger download
    const link = document.createElement("a");
    link.download = "fluid-light.png";
    tempCanvas.toBlob((blob) => {
      if (blob) {
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }, "image/png");
  } else {
    // Fallback to canvas.toBlob for simpler usage
    const link = document.createElement("a");
    link.download = "fluid-light.png";
    canvas.toBlob((blob) => {
      if (blob) {
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }, "image/png");
  }
}

/**
 * Creates a new render target with the current window dimensions.
 *
 * @param gl - WebGL context
 * @returns A new render target
 */
export function createRenderTarget(gl: OGLRenderingContext): RenderTarget {
  return new RenderTarget(gl, {
    width: window.innerWidth * devicePixelRatio,
    height: window.innerHeight * devicePixelRatio,
  });
}

/**
 * Creates a mesh with the given geometry and shader program.
 *
 * @param gl - WebGL context
 * @param geometry - Geometry to use for the mesh
 * @param program - Shader program to use for the mesh
 * @returns A new mesh instance
 */
export function createMesh(
  gl: OGLRenderingContext,
  geometry: Geometry,
  program: Program
): Mesh {
  return new Mesh(gl, {
    geometry,
    program,
  });
}

/**
 * Creates a basic quad geometry for full-screen rendering.
 *
 * @param gl - WebGL context
 * @returns A new geometry instance with position and UV attributes
 */
export function createGeometry(gl: OGLRenderingContext): Geometry {
  return new Geometry(gl, {
    position: { size: 2, data: new Float32Array([-1, -1, 3, -1, -1, 3]) },
    uv: { size: 2, data: new Float32Array([0, 0, 2, 0, 0, 2]) },
  });
}

/**
 * Deep merge utility function for merging shader configurations.
 * This function recursively merges nested objects, with source properties taking precedence.
 *
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @returns A new object with merged properties
 */
export function mergeDeep(target: any, source: any): any {
  const result = { ...target };

  for (const key in source) {
    if (source[key] !== undefined) {
      if (
        typeof source[key] === "object" &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        result[key] = mergeDeep(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}
